import React, { ReactNode } from "react";
import styled from "styled-components/macro";
import CloseIcon from "@mui/icons-material/Close";
import { Button } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { devicePaddings } from "../../tools/device";
import { useSubscriptions } from "../../root/profile/subscription/subscriptionHooks";
import { useStoreIds } from "../../root/profile/subscription/subscriptionIds";
import TransVariable from "../other/TransVariable";
import AdPaywallComparisonGrid from "./AdPaywallComparisonGrid";

const { top, bottom } = devicePaddings();

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: ${({ theme }) => theme.duo.maxWidth.narrow};
  margin: 0 auto;
  padding: ${top + 20}px 20px ${bottom + 20}px;
  height: 100%;
  width: 100%;
  background: linear-gradient(180deg, #00a1e6 0%, #d4ecff 100%);
  position: relative;
  color: ${({ theme }) => theme.duo.color.text};
  z-index: 1;
`;

const Header = styled.header`
  display: flex;
  justify-content: flex-end;
`;

const CloseButton = styled.button<{ $isVisible: boolean }>`
  min-width: 24px;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  color: ${({ theme }) => theme.duo.palette.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $isVisible }) => ($isVisible ? 0.3 : 0)};
  transition: opacity 2.5s;
  pointer-events: ${({ $isVisible }) => ($isVisible ? "auto" : "none")};
  &:hover {
    opacity: 1;
  }
  svg {
    font-size: 20px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-family: "Noto Sans", "Segoe UI", sans-serif;
  color: ${({ theme }) => theme.duo.palette.white};
  margin: 0 0 15px;
  text-align: center;
`;

const UnderlineWrapper = styled.div`
  position: relative;
  width: 187px;
  height: 7px;
  margin: 0 auto;
`;

const Underline = styled.div`
  width: 100%;
  height: 4px;
  background-color: ${({ theme }) => theme.duo.palette.white};
  border-radius: 2px;
  transform: rotate(-0.736deg);
`;

const TitleSection = styled.div`
  text-align: center;
`;

const PremiumTitle = styled.h2`
  font-size: 30px;
  color: ${({ theme }) => theme.duo.palette.white};
  font-family: "Baloo Chettan 2", system-ui, sans-serif;
  font-weight: ${({ theme }) => theme.duo.fontWeight.w700};
  text-align: center;
  margin: 20px 0;
`;

const ButtonContainer = styled.div`
  width: 100%;
  text-align: center;
  margin-top: 20px;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const TrialButton = styled(Button)`
  && {
    background-color: ${({ theme }) => theme.duo.palette.greenMedium};
    color: ${({ theme }) => theme.duo.palette.white};
    text-transform: uppercase;
    font-family: Roboto, "Segoe UI", sans-serif;
    font-weight: ${({ theme }) => theme.duo.fontWeight.w700};
    font-size: 14px;
    padding: 11px 20px;
    border-radius: 100px;
    margin-bottom: 10px;
    white-space: nowrap;
  }
`;

const TrialInfo = styled.div`
  font-size: ${({ theme }) => theme.duo.fontSize.small};
  margin: 5px 0;
`;

type Props = {
  onClose: VoidFunction;
  isCloseVisible: boolean;
  onSubscribe: (e: React.MouseEvent) => void;
  title: ReactNode | string;
  isAbTestEnabled: boolean;
};

const InternalAdPaywallDefault: React.FC<Props> = ({
  onClose,
  isCloseVisible,
  onSubscribe,
  title,
  isAbTestEnabled
}) => {
  const { t } = useTranslation();
  const { subscriptions } = useSubscriptions();
  const { storeIdYear } = useStoreIds();

  const trialAvailable = subscriptions?.[storeIdYear]?.isTrial;

  return (
    <Wrap>
      <Header>
        <CloseButton onClick={onClose} aria-label="Close premium offer" $isVisible={isCloseVisible}>
          <CloseIcon />
        </CloseButton>
      </Header>

      <Content>
        <TitleBlock>
          <TitleSection>
            <Title>{title}</Title>
            {!isAbTestEnabled && (
              <UnderlineWrapper>
                <Underline />
              </UnderlineWrapper>
            )}
          </TitleSection>

          {!isAbTestEnabled && <PremiumTitle>DuoCards Premium</PremiumTitle>}
        </TitleBlock>

        <AdPaywallComparisonGrid />
      </Content>

      <ButtonContainer>
        <TrialButton variant="contained" fullWidth onClick={onSubscribe} disabled={!subscriptions}>
          {trialAvailable ? <Trans>Start free trial</Trans> : <Trans>Subscribe</Trans>}
        </TrialButton>
        {subscriptions && (
          <TrialInfo>
            {
              // TODO: add sale logic if this paywall will be used in sales
              trialAvailable ? (
                <Trans>
                  7 days free, then <TransVariable text={subscriptions[storeIdYear]?.price + " / " + t("1 Year")} />
                </Trans>
              ) : (
                <>
                  {subscriptions[storeIdYear]?.price} / <Trans>1 Year</Trans>
                </>
              )
            }
          </TrialInfo>
        )}
      </ButtonContainer>
    </Wrap>
  );
};

export default InternalAdPaywallDefault;
